import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api/client';
import { useVisiblePolling } from './useVisiblePolling';
import { useRefreshOnReturn } from './useRefreshOnReturn';
import { getSseBus } from '../lib/sseBus';
import { diag } from '../lib/diag';
import type { V2Response } from '../api/types';

/**
 * Live chat-pane data for one session.
 *
 * Now SSE-driven (Cloud's IACP cc91e50e): subscribes to
 * `session.pane_dirty` events filtered by `sessionName` and fires a
 * targeted `/v2` fetch when the backend pings — kills the 5 s chat-lag.
 *
 * Polling stays as a fallback at 30 s (was 5 s) so SSE drops still get
 * caught eventually. The pane_dirty subscription cancels itself when
 * the session changes, so a stale-fetch race can't write the wrong
 * agent's data into the new view.
 */
// pollIntervalMs defaults to 0 = NO periodic poll (Alex 2026-06-19): the
// main views are purely SSE-driven (pane_dirty) + a catch-up on tab-
// return. A caller that lives OUTSIDE the SSE bus and needs a guaranteed
// cadence (QueueChat widget) can still opt in by passing an interval.
// Stale-while-revalidate cache (Alex 2026-07-03, click→view perf): last known good
// (non-empty) /v2 per session, module-scope so it survives view switches. On switch we
// paint the cached view INSTANTLY (0ms perceived) and revalidate in the background —
// instead of data=null → "Loading…" → 0.3–0.5s blank on every switch (worse on flaky
// net). Seeding hashRef from the cache also lets the server answer `unchanged` (tiny
// payload) when nothing moved. Capped to the ~30 most recent sessions.
const v2Cache = new Map<string, V2Response>();
const V2_CACHE_MAX = 30;
function cachePut(name: string, res: V2Response) {
  v2Cache.delete(name); // re-insert → newest position
  v2Cache.set(name, res);
  if (v2Cache.size > V2_CACHE_MAX) {
    const oldest = v2Cache.keys().next().value;
    if (oldest !== undefined) v2Cache.delete(oldest);
  }
}

export function useSessionV2(sessionName: string | null, pollIntervalMs = 0) {
  const [data, setData] = useState<V2Response | null>(null);
  // Für die Notbremse: aktueller Stand ohne Neuanlage des Intervalls.
  const dataRef = useRef(data);
  dataRef.current = data;
  const [error, setError] = useState<string | null>(null);
  const hashRef = useRef<string | undefined>(undefined);

  // Synchronous reset when sessionName changes. Doing this in a useEffect
  // leaves one render where AgentDetail sees the new sessionName but the
  // previous agent's data — the user saw the wrong conversation for the
  // ~3s until the next poll returned. Comparing inside the render body
  // and triggering setState there short-circuits React into an immediate
  // re-render with data=null, so the stale content never paints.
  // Timing instrumentation (Alex 2026-06-18: "click → content sometimes
  // 10-30s, then fast"). selectTsRef stamps the moment of the click (the
  // sessionName change); firstContentLoggedRef ensures we log the
  // click→content delta exactly once per selection. Backend v2 is
  // sub-second (measured), so a large delta isolates client-side cost
  // (cold bundle / SW precache / mermaid chunk).
  const selectTsRef = useRef<number>(0);
  const firstContentLoggedRef = useRef<boolean>(false);
  // Self-heal retry for the CRITICAL first paint (Alex 2026-07-03 "manchmal weiß, ich
  // weiß nicht wieso"): the main view has NO periodic poll (pollIntervalMs=0) — after a
  // failed or empty fetch the only re-triggers are pane_dirty (only fires when the pane
  // CHANGES — an idle agent stays silent) and tab-refocus. So one flaky-net miss froze
  // the view blank until F5. Retry with backoff (2s/4s/8s/8s) until content arrives.
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [storedSession, setStoredSession] = useState(sessionName);
  if (storedSession !== sessionName) {
    setStoredSession(sessionName);
    // Stale-while-revalidate: paint the last known view of THIS session immediately
    // (or null → "Loading…" if never seen); the fetch below revalidates. Seeding the
    // hash lets the server short-circuit with `unchanged` when nothing moved.
    const cached = sessionName ? v2Cache.get(sessionName) : undefined;
    setData(cached ?? null);
    setError(null);
    hashRef.current = cached?.hash;
    selectTsRef.current = performance.now();
    firstContentLoggedRef.current = false;
    retryCountRef.current = 0;
    if (retryTimerRef.current) { clearTimeout(retryTimerRef.current); retryTimerRef.current = null; }
  }

  // Track the "currently relevant" session name in a ref so async
  // responses can check on arrival whether they're still wanted. Without
  // this, a fetch(A) that was already in-flight when the user switched
  // to B would write A's data into the B view 1–4 s later — Alex's
  // "4 Sek alter Content vom anderen Agent" race.
  const activeNameRef = useRef(sessionName);
  activeNameRef.current = sessionName;

  // Schedule a backoff retry of refresh() for the same session. refreshRef avoids a
  // circular dep (refresh ↔ scheduleRetry); the session guard drops stale timers.
  const refreshRef = useRef<() => void>(() => {});
  const scheduleRetry = useCallback((issuedFor: string) => {
    if (retryCountRef.current >= 4 || retryTimerRef.current) return;
    const delay = Math.min(8000, 2000 * 2 ** retryCountRef.current);
    retryCountRef.current += 1;
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      if (activeNameRef.current === issuedFor) refreshRef.current();
    }, delay);
  }, []);

  const refresh = useCallback(async () => {
    if (!sessionName) return;
    const issuedFor = sessionName;
    const fetchStart = performance.now();
    try {
      const res = await api.sessions.v2(issuedFor, 500, hashRef.current);
      const fetchMs = Math.round(performance.now() - fetchStart);
      // Stale-response guard: if the user switched away while this
      // request was in flight, drop the result — don't pollute the
      // new agent's view.
      if (activeNameRef.current !== issuedFor) return;
      // Empty view (no turns) → self-heal retry: an idle agent emits no pane_dirty, so
      // without this a transient empty scrape would sit blank forever (no periodic poll).
      if (!res.unchanged && !res.turns?.length) scheduleRetry(issuedFor);
      else if (res.turns?.length) retryCountRef.current = 0;
      if (!res.unchanged) {
        setData(res);
        if (res.turns?.length) cachePut(issuedFor, res); // only good views (SWR cache)
        // Don't hash-cache an EMPTY result. A transient empty /v2 (scrape/forward glitch,
        // esp. remote-node agents on a flaky link) would otherwise hash-stabilize and the
        // view stays BLANK until the user F5s (Alex's "weißer Screen, F5 3×" 2026-07-02).
        // Leaving the hash unset re-fetches fresh next poll → self-heals when content is
        // available, without a manual reload.
        hashRef.current = res.turns?.length ? res.hash : undefined;
        // Measure click→content exactly once per selection. fetchMs is
        // the network/backend slice; clickToContentMs - fetchMs is the
        // queueing/SSE/parse overhead on top. A big gap with a small
        // fetchMs ⇒ the cost is NOT the v2 request (= bundle/chunk/cache).
        if (!firstContentLoggedRef.current && selectTsRef.current) {
          firstContentLoggedRef.current = true;
          diag('agent-detail: first content painted', {
            agent: issuedFor,
            clickToContentMs: Math.round(performance.now() - selectTsRef.current),
            fetchMs,
            bytes: JSON.stringify(res).length,
          });
        } else if (fetchMs > 1500) {
          diag('agent-detail: slow v2 fetch', { agent: issuedFor, fetchMs });
        }
      }
      setError(null);
    } catch (e) {
      if (activeNameRef.current !== issuedFor) return;
      setError(e instanceof Error ? e.message : 'Failed to load session');
      // Failed fetch (flaky net / node mid-restart) → same self-heal retry.
      scheduleRetry(issuedFor);
    }
  }, [sessionName, scheduleRetry]);
  refreshRef.current = refresh;

  // Kick off one fetch immediately on session change. Without this the
  // user waits up to intervalMs for the visible-polling loop to
  // refresh — the empty-state would linger for no good reason.
  useEffect(() => {
    if (sessionName) refresh();
  }, [sessionName, refresh]);

  // STILLE-PUSH-NOTBREMSE (Alex/Cloud 18.08., Davids Instanz): Live-Updates
  // hängen AUSSCHLIESSLICH an `session.pane_dirty` über SSE. Bleibt dieser
  // Kanal für einen Agenten stumm — bei Davids REMOTE-Codex auf PC1 (ConPTY,
  // kein tmux, über den cloud-api→cloud-api-Proxy) ist genau das der Fall —,
  // steht die Ansicht bis zum manuellen Neuladen still, obwohl der Agent
  // längst antwortet.
  //
  // Notbremse statt Rückkehr zum Dauer-Polling: NUR solange ein Zug sichtbar
  // LÄUFT (letzter Turn endet auf Werkzeug/Denken statt Text) UND seit dem
  // letzten Push mehr als PUSH_SILENCE_MS vergangen sind, wird alle
  // FALLBACK_MS nachgefragt. Sobald der Zug fertig ist oder der Push-Kanal
  // wieder liefert, hört es von selbst auf — die Last bleibt auf „ein Agent,
  // den gerade jemand ansieht, während er arbeitet" begrenzt.
  const lastPushRef = useRef<number>(0);
  useEffect(() => {
    if (!sessionName) return;
    const PUSH_SILENCE_MS = 15_000;
    const FALLBACK_MS = 4_000;
    const inFlight = () => {
      const turns = dataRef.current?.turns;
      if (!turns?.length) return false;
      const sections = turns[turns.length - 1].sections || [];
      for (let i = sections.length - 1; i >= 0; i--) {
        const k = sections[i].kind;
        if (k === 'text' || k === 'json') return false;   // Zug hat geantwortet
        if (k === 'tool' || k === 'thinking') return true; // Zug arbeitet noch
      }
      return false;
    };
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastPushRef.current < PUSH_SILENCE_MS) return; // Push lebt
      if (!inFlight()) return;                                        // nichts läuft
      void refresh();
    }, FALLBACK_MS);
    return () => window.clearInterval(id);
  }, [sessionName, refresh]);

  // SSE-driven targeted fetch: backend emits session.pane_dirty when the
  // tmux scraper sees a hash-change for this session. We re-fetch /v2
  // on the spot instead of waiting for the polling tick. Filtered by
  // sessionName so we don't waste fetches on other agents' updates.
  useEffect(() => {
    if (!sessionName) return;
    const bus = getSseBus();
    return bus.subscribe('session.pane_dirty', (ev) => {
      if (ev.name !== sessionName) return;
      lastPushRef.current = Date.now();   // Push lebt → Notbremse bleibt aus
      void refresh();
    });
  }, [sessionName, refresh]);

  // Catch-up when the tab regains focus or the network returns — covers
  // a silently-stale SSE without a periodic poll. F5 is the manual backstop.
  useRefreshOnReturn(refresh);
  // Optional periodic poll — only when a caller explicitly opts in
  // (pollIntervalMs > 0, e.g. QueueChat outside the SSE bus). No-op at 0.
  useVisiblePolling(refresh, pollIntervalMs);

  return { data, error, refresh };
}
